import { IS_BRAND_PULSZ } from 'config/brands'

import { isLoyaltyMode } from '../config'
import { splitBonusLabelText } from '../utils'

import Base from './Base'
import paletteJson from './wheelPalette.json'

import type { WHEEL_MODES } from '../config'

type CanvasLG = Parameters<CanvasRenderingContext2D['createLinearGradient']>
type CanvasRG = Parameters<CanvasRenderingContext2D['createRadialGradient']>
type CanvasFillStyle = CanvasRenderingContext2D['fillStyle']

type GradientConfig = Record<`variant${number}`, string> & {
  entries: {
    d: number | number[]
    c: Exclude<keyof GradientConfig, 'entries'>
  }[]
}

export type WheelPalette = InstanceType<typeof Wheel>['palette']

export default class Wheel extends Base {
  config = {
    lights: 16,
  }

  contentData: {
    segments: {
      label: string
      top: boolean
    }[]
    winIndex?: number
  } = null

  metrics = {
    x: 0, // computed
    y: 0, // computed
    r: 0, // computed
    paddingTop: 0, // computed
    topBar: {
      fs: 23,
    },
    border: {
      w: 0, // computed
      layer1W: 2,
      layer2W: 16,
      layer3W: 2,
      lightsR: 5,
    },
    button: {
      outerR: 45,
      innerR: 37,
      fs: 27,
    },
    content: {
      r: 0, // computed
      fs: 28,
    },
    pointer: {
      x: 0, // computed
      y: 25,
      r1: 21,
      r2: 18,
      r3: 16,
      r4: 8,
      r5: 6,
      y1: 59,
      y2: 56,
      y3: 53,
      shift: 5,
    },
    rewardContent: {
      layerH: 134,
    },
  }

  wheelImg: HTMLImageElement | null = null
  buttonImg: HTMLImageElement | null = null
  mode: WHEEL_MODES | null = null

  palette = paletteJson

  private paletteMethods = {
    getSegmentFillStyle: (i: number, params: CanvasLG) => {
      const config = this.palette.segmentFillStyles[i % this.palette.segmentFillStyles.length]
      const lg = this.ctx.createLinearGradient(...params)
      this.paletteMethods.addGradientColors(lg, config as GradientConfig)

      return lg
    },
    getSegmentFontStyle: (i: number) => {
      const index = i % this.palette.segmentFontStyles.length
      const fontFillStyle = this.palette.segmentFontStyles[index]
      const fontStrokeStyle = this.palette.segmentFontOutline[index]

      return {
        fontFillStyle,
        fontStrokeStyle,
      }
    },
    addGradientColors: (g: CanvasGradient, config: GradientConfig) => {
      config?.entries.forEach((entry) => {
        const color = config[entry.c]
        if (Array.isArray(entry.d)) {
          entry.d.forEach((pos) => {
            g.addColorStop(pos, color)
          })
        } else {
          g.addColorStop(entry.d, color)
        }
      })
    },
    getLinearGradient: (params: CanvasLG, configKey: keyof typeof this.palette) => {
      const config = this.palette[configKey]
      const lg = this.ctx.createLinearGradient(...params)
      this.paletteMethods.addGradientColors(lg, config as GradientConfig)

      return lg
    },
    getRadialGradient: (params: CanvasRG, configKey: keyof typeof this.palette) => {
      const config = this.palette[configKey]
      const rg = this.ctx.createRadialGradient(...params)
      this.paletteMethods.addGradientColors(rg, config as GradientConfig)

      return rg
    },
  }

  protected drawTopBar() {
    const { toRad } = this.constructor.helpers
    const ctx = this.ctx
    const dpr = this.canvasParams.dpr
    const { x, y, r, paddingTop } = this.metrics
    const { fs } = this.metrics.topBar
    const { getLinearGradient } = this.paletteMethods
    const fillStyleText = this.palette.topBarText

    const angleOrigin = 270
    const angleShift = 47
    // TODO: Move these variables to ysi-ui-config for better brand management
    const isLoyalty = isLoyaltyMode(this.mode)
    const isPulszLoyalty = IS_BRAND_PULSZ && isLoyalty

    const textSideL = (isPulszLoyalty ? 'spin' : IS_BRAND_PULSZ ? 'your' : 'daily').toUpperCase().split('')
    const textSideR = (isPulszLoyalty ? 'to win' : 'prize').toUpperCase().split('')
    const textR = r

    const drawLetter = ({
      letter,
      dx,
      dy,
      letterRotateAngle,
    }: {
      letter: string
      dx: number
      dy: number
      letterRotateAngle: number
    }) => {
      ctx.fillStyle = fillStyleText
      ctx.font = `700 ${fs}px Roboto, sans-serif`
      ctx.textBaseline = 'bottom'
      ctx.textAlign = 'center'
      ctx.shadowOffsetX = 1 * dpr
      ctx.shadowOffsetX = 1 * dpr
      ctx.shadowColor = fillStyleText
      ctx.translate(dx, dy)
      ctx.rotate(toRad(letterRotateAngle))
      ctx.translate(-dx, -dy)
      ctx.fillText(letter, dx, dy)
      ctx.resetTransform()
    }

    const drawLayer = <PaletteKey extends keyof typeof this.palette>({
      angle1,
      angle2,
      arcR,
      layerName,
    }: {
      arcR: number
      angle1: number
      angle2: number
      layerName: PaletteKey
    }) => {
      const angleRad1 = toRad(angle1)
      const angleRad2 = toRad(angle2)
      const lg = getLinearGradient(
        [
          x + r * Math.cos(angleRad1),
          y + r * Math.sin(angleRad1),
          x + r * Math.cos(angleRad2),
          y + r * Math.sin(angleRad2),
        ],
        layerName,
      )
      ctx.beginPath()
      ctx.moveTo(x, y)
      ctx.arc(x, y, arcR, angleRad1, angleRad2)
      ctx.closePath()
      ctx.fillStyle = lg
      ctx.fill()
    }

    drawLayer({
      angle1: angleOrigin - angleShift,
      angle2: angleOrigin + angleShift,
      arcR: r + paddingTop,
      layerName: 'topBarLayer1',
    })

    const angleShiftLayer2 = angleShift - 0.7
    drawLayer({
      angle1: angleOrigin - angleShiftLayer2,
      angle2: angleOrigin + angleShiftLayer2,
      arcR: r + paddingTop - 2 * dpr,
      layerName: 'topBarLayer2',
    })

    drawLayer({
      angle1: angleOrigin - angleShift,
      angle2: angleOrigin + angleShift,
      arcR: r + 1 * dpr,
      layerName: 'topBarLayer3',
    })

    // TODO: Move these variables to ysi-ui-config for better brand management
    const textPaddingX = isPulszLoyalty ? 9 : 7
    const letterSpace = isPulszLoyalty ? 6.5 : 6.5
    const letterSpaceR = isPulszLoyalty ? 5 : 6.5

    textSideL.forEach((letter, i) => {
      const letterRotateAngle = angleShift - textPaddingX - letterSpace * i
      const angle = toRad(angleOrigin - letterRotateAngle)
      const dx = x + textR * Math.cos(angle)
      const dy = y + textR * Math.sin(angle)

      drawLetter({
        letter,
        dx,
        dy,
        letterRotateAngle: -letterRotateAngle,
      })
    })

    textSideR.reverse().forEach((letter, i) => {
      const letterRotateAngle = angleShift - textPaddingX - letterSpaceR * i
      const angle = toRad(angleOrigin + letterRotateAngle)
      const dx = x + textR * Math.cos(angle)
      const dy = y + textR * Math.sin(angle)

      drawLetter({
        letter,
        dx,
        dy,
        letterRotateAngle,
      })
    })

    // restore ctx
    ctx.textAlign = 'start'
    ctx.textBaseline = 'alphabetic'
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetX = 0
    ctx.shadowColor = '#000'
  }

  protected drawBorder() {
    const ctx = this.ctx
    const { x, y, r } = this.metrics

    if (this.wheelImg) {
      ctx.drawImage(this.wheelImg, 0, y - r, this.canvasParams.w, this.canvasParams.h - 30 * this.canvasParams.dpr)

      return
    }

    const { toRad } = this.constructor.helpers

    const { layer1W, layer3W, layer2W } = this.metrics.border

    const getGradient = (configKey: keyof typeof this.palette) => {
      const angle = 90

      return this.paletteMethods.getLinearGradient(
        [
          x - r * Math.cos(toRad(angle)),
          y - r * Math.sin(toRad(angle)),
          x + r * Math.cos(toRad(angle)),
          y + r * Math.sin(toRad(angle)),
        ],
        configKey,
      )
    }

    const drawFilledCircle = (circleR: number, fillStyle: CanvasFillStyle) => {
      ctx.beginPath()
      ctx.arc(x, y, circleR, 0, 2 * Math.PI)
      ctx.fillStyle = fillStyle
      ctx.fill()
    }

    let circleShift = 0
    drawFilledCircle(r, getGradient('borderLayer1LG'))
    circleShift += layer1W

    drawFilledCircle(r - circleShift, getGradient('borderLayer2LG'))
    circleShift += layer2W

    drawFilledCircle(r - circleShift, getGradient('borderLayer3LG'))
    circleShift += layer3W
  }

  protected drawContent(params = { rotationAngle: 0, fadeProgress: 0 }) {
    const { toRad, isSafari } = this.constructor.helpers
    const ctx = this.ctx
    const dpr = this.canvasParams.dpr
    const { x, y } = this.metrics
    const { r, fs } = this.metrics.content
    const { segments, winIndex } = this.contentData
    const { variant1: borderColor } = this.palette.borderLayer3LG
    const { getSegmentFontStyle, getSegmentFillStyle, getLinearGradient, getRadialGradient } = this.paletteMethods
    const borderWidth = this.metrics.border.layer3W
    const a = 360 / segments.length
    const segmentAngleL = toRad(270 - a / 2)
    const segmentAngleR = toRad(270 + a / 2)
    const fadeAlpha = isSafari() ? 0.6 : 0.4

    // Create gold gradients for "top prize" section
    const createGoldLinearGradient = () => {
      const lg = this.ctx.createLinearGradient(x, y - r, x, y + r)
      lg.addColorStop(0.0, '#6A3C09')
      lg.addColorStop(0.23, '#F8E6B6')
      lg.addColorStop(1.0, '#B28F53')

      return lg
    }

    const createGoldRadialGradient = () => {
      const rg = this.ctx.createRadialGradient(x, y, r * 0.1, x, y, r)
      rg.addColorStop(0.4, '#AA8037')
      rg.addColorStop(0.5, '#FBEFCF')
      rg.addColorStop(0.82, '#B8945A')
      rg.addColorStop(0.9, '#FFDB83')
      rg.addColorStop(1.0, '#8B4F2A')

      return rg
    }

    const textX = x + r * Math.cos(toRad(270))
    const textY = y + r * Math.sin(toRad(270))
    const fs2 = fs * 0.6
    const letterSpacing1 = -dpr * 0.5
    const letterSpacing2 = -dpr * 0.3
    const textShift1 = fs * 1.35
    const textShift2 = textShift1 + fs2
    const textShift3 = textShift2 + fs
    const topPrizeTextShift2 = fs * 0.2
    const topPrizeTextShift3 = fs * 0.4

    const getCtxFont = (_fs: number) => `normal ${_fs}px BebasNeue, sans-serif`

    segments.forEach((segment, i) => {
      const isBlurred = params.fadeProgress && i !== winIndex
      const { fontFillStyle, fontStrokeStyle } = getSegmentFontStyle(i)
      const fontStrokeWidth = 3 * dpr
      const rotationAngle = toRad(a * i) + toRad(params.rotationAngle)
      const {
        firstSubstring: gcPart,
        secondSubstring: freePart,
        thirdSubstring: scPart,
      } = splitBonusLabelText(segment.label)
      const isLoyalty = isLoyaltyMode(this.mode)
      const isTopPrizeSegment = segment.top

      ctx.translate(x, y)
      ctx.rotate(rotationAngle)
      ctx.translate(-x, -y)
      // segment layer
      ctx.beginPath()
      ctx.moveTo(x, y)
      ctx.arc(x, y, r, segmentAngleL, segmentAngleR)
      ctx.lineTo(x, y)
      // Use gold gradients for the specified section
      if (isTopPrizeSegment) {
        if (isLoyalty) {
          ctx.fillStyle = getLinearGradient([x, y - r, x, y + r], 'topPrizeSegmentLG')
          ctx.fill()

          ctx.fillStyle = getRadialGradient([x, y, r * 0.1, x, y, r], 'topPrizeSegmentRG')
          ctx.beginPath()
          ctx.moveTo(x, y)
          ctx.arc(x, y, r, segmentAngleL, segmentAngleR)
          ctx.lineTo(x, y)
          ctx.fill()
        } else {
          ctx.fillStyle = createGoldLinearGradient()
          ctx.fill()
          // Overlay radial gradient for metallic shine
          ctx.globalAlpha = 1
          ctx.fillStyle = createGoldRadialGradient()
          ctx.beginPath()
          ctx.moveTo(x, y)
          ctx.arc(x, y, r, segmentAngleL, segmentAngleR)
          ctx.lineTo(x, y)
          ctx.fill()
          ctx.globalAlpha = 1
        }
      } else {
        ctx.fillStyle = getSegmentFillStyle(i, [x, y, x, y - r])
        ctx.fill()
      }

      // segment borders
      ctx.beginPath()
      ctx.moveTo(x, y)
      ctx.lineTo(x + r * Math.cos(segmentAngleL), y + r * Math.sin(segmentAngleL))
      ctx.moveTo(x, y)
      ctx.lineTo(x + r * Math.cos(segmentAngleR), y + r * Math.sin(segmentAngleR))
      ctx.strokeStyle = borderColor
      ctx.lineWidth = borderWidth
      ctx.stroke()

      // text content
      ctx.filter = isBlurred ? `blur(${1.5 * dpr * params.fadeProgress}px)` : 'none'
      // Use custom color for top prize segment
      const textFillStyle = isTopPrizeSegment ? (isLoyalty ? '#FDD982' : '#4D0100') : fontFillStyle
      // const fontSizeMultiplier = isTopPrizeSegment ? 1 : 0.9
      const fontSizeMultiplier = 0.85
      const textStrokeStyle = isTopPrizeSegment ? '#F8E6B6' : fontStrokeStyle
      ctx.fillStyle = textFillStyle
      ctx.strokeStyle = textStrokeStyle
      ctx.lineWidth = fontStrokeWidth
      ctx.textAlign = 'center'
      ctx.textBaseline = 'bottom'
      // text 1 line
      ctx.font = getCtxFont(fs * fontSizeMultiplier)
      ctx.letterSpacing = `${letterSpacing1}px`
      if (!isTopPrizeSegment || !isLoyalty) {
        ctx.strokeText(gcPart, textX, textY + textShift1)
      }
      ctx.fillText(gcPart, textX, textY + textShift1)
      // text 2 line
      ctx.font = scPart ? getCtxFont(fs2 * fontSizeMultiplier) : getCtxFont(fs * fontSizeMultiplier)
      ctx.letterSpacing = `${letterSpacing2}px`

      let secondLineYOffset
      if (scPart) {
        // secondLineYOffset = isTopPrizeSegment ? textShift2 - topPrizeTextShift2 : textShift2
        secondLineYOffset = textShift2 - topPrizeTextShift2
      } else {
        secondLineYOffset = textShift1 + fs
      }

      if (!isTopPrizeSegment || !isLoyalty) {
        ctx.strokeText(freePart, textX, textY + secondLineYOffset)
      }
      ctx.fillText(freePart, textX, textY + secondLineYOffset)
      // text 3 line
      if (scPart) {
        ctx.font = getCtxFont(fs * fontSizeMultiplier)
        ctx.letterSpacing = `${letterSpacing1}px`
        if (!isTopPrizeSegment || !isLoyalty) {
          // ctx.strokeText(scPart, textX, textY + (isTopPrizeSegment ? textShift3 - topPrizeTextShift3 : textShift3))
          ctx.strokeText(scPart, textX, textY + (textShift3 - topPrizeTextShift3))
        }
        // ctx.fillText(scPart, textX, textY + (isTopPrizeSegment ? textShift3 - topPrizeTextShift3 : textShift3))
        ctx.fillText(scPart, textX, textY + (textShift3 - topPrizeTextShift3))
      }
      // reset segment transform
      ctx.resetTransform()
      // restore ctx
      ctx.filter = 'none'
      ctx.lineWidth = 1
      ctx.strokeStyle = '#000'
      ctx.letterSpacing = '0px'
      ctx.textAlign = 'start'
      ctx.textBaseline = 'alphabetic'

      // fading
      // separated because of the Chrome layering deffect
      if (isBlurred) {
        ctx.translate(x, y)
        ctx.rotate(rotationAngle)
        ctx.translate(-x, -y)
        ctx.beginPath()
        ctx.moveTo(x, y)
        ctx.arc(x, y, r, segmentAngleL, segmentAngleR)
        ctx.lineTo(x, y)
        ctx.fillStyle = `rgba(0,0,0,${fadeAlpha * params.fadeProgress})`
        ctx.fill()
        ctx.resetTransform()
      }
    })
  }

  protected drawLights(lightsAlpha: Record<number, number> = {}) {
    if (this.wheelImg) {
      return
    }

    const ctx = this.ctx
    const { toRad } = this.constructor.helpers
    const { x, y, r } = this.metrics
    const lightsN = this.config.lights
    const { getRadialGradient } = this.paletteMethods
    const { layer1W, layer2W, layer3W, lightsR } = this.metrics.border
    const borderW = layer1W + layer2W + layer3W

    ;[...Array(lightsN)].forEach((_, i) => {
      const angle = toRad(360 / lightsN) * i
      const x0 = x + (r - borderW / 2) * Math.cos(angle)
      const y0 = y + (r - borderW / 2) * Math.sin(angle)
      const fillStyle = getRadialGradient([x0, y0, lightsR, x0, y0, 0], 'borderLightsRG')

      const lightR1 = lightsR * 4
      const lightR2 = lightsR * 5
      const rg1 = ctx.createRadialGradient(x0, y0, lightR1, x0, y0, 0)
      rg1.addColorStop(0, 'rgba(255,255,255,0)')
      rg1.addColorStop(1, 'rgba(255,255,255,0.85)')
      const rg2 = ctx.createRadialGradient(x0, y0, lightR2, x0, y0, 0)
      rg2.addColorStop(0, 'rgba(255,255,255,0)')
      rg2.addColorStop(1, 'rgba(255,255,255,0.65)')

      ctx.fillStyle = rg1
      // ctx.fillStyle = '#f00'
      ctx.globalAlpha = lightsAlpha[i] ? lightsAlpha[i] : 0
      ctx.beginPath()
      ctx.arc(x0, y0, lightR1, 0, 2 * Math.PI)
      ctx.fill()
      ctx.fillStyle = rg2
      // ctx.fillStyle = '#f00'
      ctx.arc(x0, y0, lightR2, 0, 2 * Math.PI)
      ctx.fill()

      // restore ctx
      ctx.globalAlpha = 1

      // static light
      ctx.fillStyle = fillStyle
      ctx.shadowColor = 'rgba(255,255,255,1)'
      ctx.shadowBlur = lightsR
      ctx.beginPath()
      ctx.arc(x0, y0, lightsR, 0, 2 * Math.PI)
      ctx.fill()
      // static shadow
      ctx.shadowColor = 'rgba(0,0,0,0.5)'
      ctx.shadowBlur = lightsR / 2
      ctx.shadowOffsetX = (-lightsR / 3) * Math.cos(angle)
      ctx.shadowOffsetY = (-lightsR / 3) * Math.sin(angle)
      ctx.fill()

      // restore ctx
      ctx.shadowColor = 'rgba(0,0,0,0)'
      ctx.shadowBlur = 0
      ctx.shadowOffsetX = 0
      ctx.shadowOffsetY = 0
    })
  }

  protected drawPointer(pointerShift = 0) {
    const { toRad } = this.constructor.helpers
    const ctx = this.ctx
    const dpr = this.canvasParams.dpr
    const { x, y, r1, r2, r3, r4, r5, y1, y2, y3 } = this.metrics.pointer
    const { getLinearGradient } = this.paletteMethods

    const drawOuterLayer = (rN: number, yN: number, fillStyle: CanvasFillStyle) => {
      ctx.beginPath()
      ctx.fillStyle = fillStyle
      ctx.moveTo(x - 0.5 * dpr, yN)
      ctx.lineTo(x + 0.5 * dpr, yN)
      ctx.lineTo(x + rN * Math.cos(toRad(35)), y + rN * Math.sin(toRad(35)))
      ctx.lineTo(x + rN * Math.cos(toRad(145)), y + rN * Math.sin(toRad(145)))
      ctx.closePath()
      ctx.fill()
      ctx.beginPath()
      ctx.arc(x, y, rN, 0, 2 * Math.PI)
      ctx.fill()
    }

    const drawInnerLayer = (rN: number, fillStyle: CanvasFillStyle) => {
      ctx.beginPath()
      ctx.fillStyle = fillStyle
      ctx.arc(x, y, rN, 0, 2 * Math.PI)
      ctx.fill()
    }

    ctx.translate(0, pointerShift)
    drawOuterLayer(r1, y1, getLinearGradient([x, y - r1, x, y1], 'pointerLayer1'))
    drawOuterLayer(r2, y2, getLinearGradient([x, r1 - r2, x, y2], 'pointerLayer2'))
    drawOuterLayer(r3, y3, getLinearGradient([x - r3, r2 - r3, x + r2, y3], 'pointerLayer3'))
    drawInnerLayer(r4, getLinearGradient([x, y - r4, x, y + r4], 'pointerLayer4'))
    drawInnerLayer(r5, getLinearGradient([x, y - r5, x, y + r5], 'pointerLayer5'))
    ctx.resetTransform()
  }

  protected drawButton(fadeProgress = 0) {
    if (fadeProgress >= 1) {
      return
    }

    const { toRad } = this.constructor.helpers
    const ctx = this.ctx
    const dpr = this.canvasParams.dpr
    const { x, y } = this.metrics
    const { outerR, innerR, fs } = this.metrics.button
    const textScaleIndex = 1.25
    const textScaleJustifyIndex = 1.2

    if (this.buttonImg && this.buttonImg.width > 0) {
      const w = outerR * 3
      const h = w * (this.buttonImg.height / this.buttonImg.width)
      // Move image higher by 15% of the outer radius
      const yOffset = outerR * 0.15

      ctx.save()
      ctx.translate(x, y - yOffset)
      ctx.drawImage(this.buttonImg, -w / 2, -h / 2, w, h)
      ctx.restore()
    } else {
      const lgOuterAngleRad = toRad(45)
      const lgOuter = this.paletteMethods.getLinearGradient(
        [
          x - outerR * Math.cos(lgOuterAngleRad),
          y - outerR * Math.sin(lgOuterAngleRad),
          x + outerR * Math.cos(lgOuterAngleRad),
          y + outerR * Math.sin(lgOuterAngleRad),
        ],
        'buttonOuterLG',
      )
      const lgInner = this.paletteMethods.getLinearGradient([x, y - innerR, x, y + innerR], 'buttonInnerLG')

      // outer circle
      ctx.fillStyle = lgOuter
      ctx.shadowColor = 'rgba(0,0,0,.4)'
      ctx.shadowBlur = 3 * dpr
      ctx.shadowOffsetX = -2 * dpr
      ctx.shadowOffsetY = 4 * dpr
      ctx.beginPath()
      ctx.arc(x, y, outerR, 0, 2 * Math.PI)
      ctx.fill()
      // inner circle
      ctx.shadowColor = 'rgba(0,0,0,.4)'
      ctx.shadowBlur = 3 * dpr
      ctx.shadowOffsetX = 0
      ctx.shadowOffsetY = 0
      ctx.fillStyle = lgInner
      ctx.strokeStyle = 'rgba(0,0,0,.1)'
      ctx.lineWidth = dpr
      ctx.beginPath()
      ctx.arc(x, y, innerR, 0, 2 * Math.PI)
      ctx.fill()
      ctx.stroke()
      // restore ctx
      ctx.shadowBlur = 0
      ctx.shadowColor = '#000'
      ctx.strokeStyle = '#000'
      // text content
      ctx.translate(x, y)
      ctx.scale(1, textScaleIndex)
      ctx.translate(-x, -y + fs * 0.5 * textScaleJustifyIndex)
      ctx.font = `700 ${fs}px Roboto, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'bottom'
      ctx.fillStyle = this.palette.buttonText
      ctx.fillText('SPIN', x, y)
      ctx.resetTransform()
    }

    // restore ctx
    ctx.textAlign = 'start'
    ctx.textBaseline = 'alphabetic'

    // fading
    if (fadeProgress) {
      ctx.fillStyle = `rgba(0,0,0,${0.4 * fadeProgress})`
      ctx.beginPath()
      ctx.arc(x, y, outerR, 0, 2 * Math.PI)
      ctx.fill()
    }
  }

  protected drawRewardContent() {
    const ctx = this.ctx
    const { x, y, r } = this.metrics
    const { w: borderW } = this.metrics.border
    const { layerH } = this.metrics.rewardContent

    const layerX = 0
    // Start slightly higher and make it taller to ensure custom button images are fully covered
    const layerY = y - this.metrics.button.outerR * 1.3
    const layerW = r * 2
    const adjustedLayerH = layerH + this.metrics.button.outerR * 0.3

    ctx.save()
    ctx.beginPath()
    ctx.arc(x, y, r - borderW, 0, 2 * Math.PI)
    ctx.clip()

    ctx.beginPath()
    ctx.fillStyle = '#fff'
    ctx.rect(layerX, layerY, layerW, adjustedLayerH)
    ctx.fill()
    ctx.restore()
  }

  draw() {
    this.ctx.clearRect(0, 0, this.canvasParams.w, this.canvasParams.h)
    // the [this.drawSomething] order matters
    this.drawTopBar()
    this.drawBorder()
    this.drawContent()
    this.drawButton()
    this.drawLights()
    this.drawPointer()
  }

  private wireMetrics() {
    type Metrics = { [key: string]: Metrics | number | number[] } | number | number[]
    ;(function withDpr(metrics: Metrics, dpr): Metrics {
      if (Array.isArray(metrics)) {
        return metrics.map((item) => withDpr(item, dpr)) as number[]
      }

      if (typeof metrics === 'object') {
        let key: keyof typeof metrics
        for (key in metrics) {
          metrics[key] = withDpr(metrics[key], dpr)
        }

        return metrics
      }

      if (typeof metrics === 'number') {
        return metrics * dpr
      }

      return metrics
    })(this.metrics, this.canvasParams.dpr)

    const canvasR = this.canvasParams.w / 2
    const { border } = this.metrics
    const borderW = border.layer1W + border.layer2W + border.layer3W

    this.metrics.paddingTop = this.canvasParams.h - this.canvasParams.w
    this.metrics.x = canvasR
    this.metrics.y = canvasR + this.metrics.paddingTop
    this.metrics.r = canvasR

    this.metrics.border.w = borderW
    this.metrics.content.r = canvasR - borderW
    this.metrics.pointer.x = canvasR
  }

  public init<ContentData extends typeof this.contentData>(
    node: HTMLCanvasElement,
    params: {
      data: ContentData
      palette?: Partial<WheelPalette>
      wheelSrc?: string
      buttonSrc?: string
      mode?: WHEEL_MODES
    },
  ) {
    this.setCanvasParams(node)

    this.contentData = params.data
    this.palette = { ...this.palette, ...params.palette }
    this.mode = params.mode || null

    if (params.buttonSrc && !this.buttonImg) {
      const img = new Image()
      img.src = params.buttonSrc
      img.onload = () => {
        this.buttonImg = img
        // Force redraw if metrics are ready
        if (this.metrics.r > 0) {
          this.draw()
        }
      }
    }

    this.wireMetrics()
  }
}
